import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Spin } from '@robinpowered/ui-kit';
import { useGetHomepage } from 'hooks/useGetHomepage';
import { useHomepagePath } from 'hooks/useHomepagePath';
import { useAuthContext, useApolloContext } from 'contexts';
import { PageMessage } from './PageMessage';
import { useTranslation } from 'react-i18next';

export const HomepageRedirect = (): JSX.Element => {
  const { t } = useTranslation('common');
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const { tenantId } = useApolloContext();
  const {
    userOrganizations,
    loading: authLoading,
    setOrgSlug,
    orgSlug: contextOrgSlug,
  } = useAuthContext();
  const { homepage, loading: homepageLoading } = useGetHomepage({
    skip: !tenantId,
  });
  const homepagePath = useHomepagePath(homepage);

  // Check if the orgSlug from URL is valid
  const isValidOrgSlug = useMemo(() => {
    if (!orgSlug) return true; // No orgSlug means we'll auto-select
    if (!userOrganizations) return null; // Still loading, can't determine yet

    return userOrganizations.some((org) => org.slug === orgSlug);
  }, [orgSlug, userOrganizations]);

  const targetOrgSlug = useMemo(() => {
    if (orgSlug) {
      return orgSlug;
    }
    if (userOrganizations && userOrganizations.length > 0) {
      return userOrganizations[0]?.slug;
    }
    return null;
  }, [orgSlug, userOrganizations]);

  const loading =
    authLoading ||
    (targetOrgSlug && !tenantId) ||
    (tenantId && homepageLoading);

  // Set orgSlug in AuthContext from URL params or auto-select first org
  // This ensures AuthContext can compute currentOrg and set tenantId
  useEffect(() => {
    // If we have a target org, set it in context (validation happens separately)
    // Only update if it's different to avoid unnecessary re-renders
    // We set it even if isValidOrgSlug is null (still loading) so AuthContext can compute currentOrg
    if (
      targetOrgSlug &&
      contextOrgSlug !== targetOrgSlug &&
      isValidOrgSlug !== false
    ) {
      setOrgSlug(targetOrgSlug);
    }
  }, [targetOrgSlug, contextOrgSlug, setOrgSlug, isValidOrgSlug]);

  // Logic to redirect to the homepage to user's preferred page.
  // Note: If we need to support 'robin-dashboard' links, then
  // you will have to redirect with window.location.href instead of navigate.
  useEffect(() => {
    if (!loading && homepagePath && targetOrgSlug && isValidOrgSlug) {
      navigate(`/${targetOrgSlug}/${homepagePath}`, { replace: true });
    }
  }, [homepagePath, loading, targetOrgSlug, navigate, isValidOrgSlug]);

  // Show error if user is not authenticated
  // Only show error if:
  // 1. The auth query is done loading (authLoading is false)
  // 2. We have no user organizations (query was skipped or failed)
  // We don't check isLoggedIn because it can be false temporarily while waiting
  // for tenantId to be set, even when we have valid data
  if (!authLoading && (!userOrganizations || userOrganizations.length === 0)) {
    return (
      <PageMessage
        title={t('errors.auth_error.title')}
        message={t('errors.auth_error.message')}
      />
    );
  }

  // Show error if orgSlug is invalid (only after we've loaded organizations)
  if (!authLoading && orgSlug && isValidOrgSlug === false) {
    return <PageMessage title={t('errors.invalid_org_slug', { orgSlug })} />;
  }

  // Show loading spinner while loading
  return (
    <CenteredContainer>{loading && <Spin size="large" />}</CenteredContainer>
  );
};

const CenteredContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
